import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { TextArea } from "@tql/ui-components-react/kendo/inputs";
import { Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { useId, useState } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostSendAccounting } from "../../hooks/use-post-send-accounting.ts";
import * as styles from "./send-to-accounting-modal.module.css";

const ACCOUNTING_NOTE_MAX_LENGTH = 200;

export const SendToAccountingModal = ({
	onClose,
	isOpen,
	poNumber,
}: {
	onClose: () => void;
	isOpen: boolean;
	poNumber: string;
}) => {
	const accountingNoteId = useId();
	const accountingNoteHintId = `${accountingNoteId}_hint`;
	const [accountingNote, setAccountingNote] = useState("");
	const { sendAccountingAsync, isSendingAccounting } = usePostSendAccounting();

	const handleChange = (event: TextAreaChangeEvent) => {
		setAccountingNote(event.value);
	};

	const handleClose = () => {
		setAccountingNote("");
		onClose();
	};

	const handleConfirm = async () => {
		try {
			await sendAccountingAsync({ poNumber, message: accountingNote });
			handleClose();
		} catch {
			// Error surfaced via the hook's notification.
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			confirmDisabled={isSendingAccounting}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={() => void handleConfirm()}
			showButtonsDisabled={isSendingAccounting}
			title={"Send to Accounting"}
			type={"confirmation"}
			width={400}
		>
			<LoadingSpinner isVisible={isSendingAccounting} />
			<div className={styles.field}>
				<Label editorId={accountingNoteId}>
					Notes for Accounting? Do not enter note below if load is GTG.
				</Label>
				<TextArea
					ariaDescribedBy={accountingNoteHintId}
					id={accountingNoteId}
					maxLength={ACCOUNTING_NOTE_MAX_LENGTH}
					onChange={handleChange}
					resizable={"none"}
					rows={4}
					value={accountingNote}
				/>
				<Hint direction={"end"} id={accountingNoteHintId}>
					{accountingNote.length}/{ACCOUNTING_NOTE_MAX_LENGTH}
				</Hint>
			</div>
		</TraxModal>
	);
};
